import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaShip, FaUserFriends, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { formatTime } from "../../services/dateUtils";
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const CustomInput = React.forwardRef(
  ({ value, onClick, placeholder, isError, icon: Icon }, ref) => (
    <div
      ref={ref}
      onClick={onClick}
      style={{
        border: isError ? "1px solid #ef4444" : "1px solid gray",
        color: isError ? "#ef4444" : "gray",
      }}
      className={`w-full h-[38px] border p-1.5 rounded text-left z-50 hover:border-blue-800 transition-colors duration-300 cursor-pointer ${
        isError ? "bg-red-50" : "bg-gray-50"
      } flex items-center gap-2`}
    >
      {Icon && <Icon className="text-gray-500" />}
      <span className="truncate">{value || placeholder}</span>
    </div>
  )
);

const CustomSelect = ({ value, onChange, options, placeholder, icon: Icon, name }) => (
  <div className="relative">
     {Icon && <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />}
    <select
      name={name} // Added name prop
      value={value}
      onChange={onChange}
      className={`w-full h-[38px] border border-gray-500 rounded bg-gray-50 text-gray-500 appearance-none hover:border-blue-800 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-900 ${Icon ? 'pl-8 pr-3' : 'px-3'}`} // Adjust padding if icon exists
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
      <svg
        className="w-4 h-4 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          d="M19 9l-7 7-7-7"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    </div>
  </div>
);


const YachtBookingForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    cruiseType: "", // Changed from vehicleType
    destination: "", // New field for destination
    bookingDate: null, // Changed from pickUpDate
    bookingTime: "", // Changed from pickUpTime
    numberOfGuests: "", // New field for guests
    couponCode: "",
  });
  const [dateErrors, setDateErrors] = useState({ booking: false }); // Simplified date errors
  const [datePickerOpen, setDatePickerOpen] = useState(false); // Simplified date picker state
  const [dateTimeComplete, setDateTimeComplete] = useState(false); // Simplified completion state
  const [timeWarning, setTimeWarning] = useState(false); // Simplified time warning

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Define options for yacht booking
  const cruiseTypeOptions = [
    { value: "half-day-morning", label: "Half-Day Morning (5 hours)" },
    { value: "half-day-sunset", label: "Half-Day Sunset (5 hours)" },
    { value: "full-day", label: "Full-Day (8 hours)" },
    // Add other cruise types if necessary
  ];

  const destinationOptions = [
    { value: "south-coast", label: "South Coastline of Mykonos" },
    { value: "delos-rhenia", label: "Delos & Rhenia Islands" },
    { value: "south-delos-rhenia", label: "South Coast & Delos/Rhenia (Full Day Only)" },
    // Add other destinations if necessary
  ];

  const guestOptions = Array.from({ length: 20 }, (_, i) => ({ // Example: up to 20 guests
    value: i + 1,
    label: `${i + 1} Guest${i > 0 ? 's' : ''}`
  }));


  // Simplified date validation
  const validateDate = (newDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (newDate < today) {
      setDateErrors({ booking: true });
      return false;
    }
    setDateErrors({ booking: false });
    return true;
  };

  // Simplified date change handler
  const handleDateChange = (date) => {
    if (!validateDate(date)) {
      return;
    }

    const hasTime = date && (date.getHours() !== 0 || date.getMinutes() !== 0); // Check if time is selected

    setDateTimeComplete(!hasTime);

    setFormData((prev) => ({
      ...prev,
      bookingDate: date,
      bookingTime: hasTime ? `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}` : '',
    }));

    if (hasTime) {
      setDatePickerOpen(false); // Close picker only if time is selected
    }
  };

  // Simplified form change handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Special handling for destination based on cruise type
    if (name === 'cruiseType') {
        if (value !== 'full-day' && formData.destination === 'south-delos-rhenia') {
            setFormData(prev => ({ ...prev, destination: '', [name]: value })); // Reset destination if not full-day
        } else {
             setFormData(prev => ({ ...prev, [name]: value }));
        }
    } else {
         setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Updated submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Basic validation (add more as needed)
    if (!formData.bookingDate || !formData.numberOfGuests) {
        setError("Please fill in all required fields.");
        setLoading(false);
        return;
    }
     if (formData.cruiseType !== 'full-day' && formData.destination === 'south-delos-rhenia') {
        setError("Combined South Coast & Delos/Rhenia destination is only available for Full-Day cruises.");
        setLoading(false);
        return;
    }


    try {
      // Navigate to search results page with yacht booking details
      navigate('/yachts', {
        state: {
          searchType: 'yacht', // Indicate this is a yacht search
          cruiseType: formData.cruiseType,
          destination: formData.destination,
          bookingDate: formData.bookingDate,
          bookingTime: formData.bookingTime,
          numberOfGuests: formData.numberOfGuests,
          couponCode: formData.couponCode,
          // Pass any other relevant data
        }
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full content-center bg-transparent">
      <div className="max-w-[1440px] mx-auto px-2 sm:px-4">
        <motion.h2
          className="md:block hidden text-3xl lg:text-5xl pb-2 sm:pb-4 pt-6 sm:pt-10 text-left font-bold"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Mykonos Yacht Charters: Experience <br />
          <span className="font-bold">Luxury on the Aegean Sea</span> {/* Updated Title */}
        </motion.h2>
        <motion.form
          onSubmit={handleSubmit}
          className="p-2 sm:p-4 md:p-6 bg-white shadow-md rounded-md border w-full relative"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-90 z-50 flex items-center justify-center">
              <LoadingSpinner message="Finding available yachts..." /> {/* Updated Loading Message */}
            </div>
          )}

          {error && (
            <motion.div
              className="mb-4 bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded"
              role="alert"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-center">{error}</p>
            </motion.div>
          )}

          {/* Removed Vehicle Selection and Car Image Section */}

          {/* Input Sections */}
          <div className="space-y-3 sm:space-y-4 md:space-y-6">
             <h3 className="text-base sm:text-lg md:text-xl font-bold text-blue-800 text-left mb-2 sm:mb-4">
                Book Your Dream Yacht Experience
              </h3>
            {/* Cruise Details Container */}
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-3 sm:gap-4 w-full">
               {/* <CustomSelect
                  name="cruiseType"
                  value={formData.cruiseType}
                  onChange={handleChange}
                  options={cruiseTypeOptions}
                  placeholder="Select Cruise Type"
                  icon={FaShip}
                /> */}
                 <CustomSelect
                  name="numberOfGuests"
                  value={formData.numberOfGuests}
                  onChange={handleChange}
                  options={guestOptions}
                  placeholder="Number of Guests"
                  icon={FaUserFriends}
                />
            </div>

             {/* Date & Time Container */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 w-full">
              <div className="w-full">
                <DatePicker
                  selected={formData.bookingDate}
                  onChange={(date) => handleDateChange(date)}
                  customInput={<CustomInput isError={dateErrors.booking} icon={FaCalendarAlt} />} // Added icon
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                  placeholderText="Select Date"
                  open={datePickerOpen}
                  onClickOutside={() => {
                    if (dateTimeComplete) {
                      setDatePickerOpen(false);
                    }
                  }}
                  onInputClick={() => setDatePickerOpen(true)}
                  wrapperClassName="w-full bg-gray-50 border-gray-300 hover:border-blue-800 ease-in-out duration-300" // Changed hover color
                />
              </div>
               <div className="w-full">
                 <input
                    type="text"
                    name="couponCode"
                    placeholder="Coupon Code (Optional)"
                    value={formData.couponCode}
                    onChange={handleChange}
                    className="w-full h-[38px] px-3 border border-gray-500 rounded bg-gray-50 text-gray-500 hover:border-blue-800 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-900" // Style consistency
                  />
               </div>
            </div>

            {/* Submit Button */}
             <div className="flex justify-end pt-2">
                 <motion.button
                    type="submit"
                    className="bg-blue-800 text-white text-sm sm:text-base py-1.5 sm:py-2 px-4 sm:px-8 rounded hover:bg-blue-900 transition tracking-widest whitespace-nowrap w-full sm:w-auto flex-shrink-0" // Updated styles
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={loading} // Disable button when loading
                  >
                    {loading ? 'Searching...' : 'Search Yachts'} {/* Updated Button Text */}
                  </motion.button>
             </div>
          </div>
        </motion.form>
      </div>
      {/* Removed animation styles if not needed */}
    </div>
  );
};

export default YachtBookingForm; // Export renamed component
